import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = { class: "schedule-page" }
const _hoisted_2 = { class: "schedule-shell" }
const _hoisted_3 = { class: "toolbar" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "date-control" }
const _hoisted_7 = { class: "filter" }
const _hoisted_8 = { class: "schedule-layout" }
const _hoisted_9 = { class: "side-panel" }
const _hoisted_10 = { class: "panel" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "panel" }
const _hoisted_13 = ["value"]
const _hoisted_14 = { class: "panel" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "panel missed-panel" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { key: 0 }
const _hoisted_19 = { class: "calendar-panel" }
const _hoisted_20 = { class: "week-header" }
const _hoisted_21 = { class: "calendar-grid" }
const _hoisted_22 = { class: "time-cell" }
const _hoisted_23 = ["onDblclick"]
const _hoisted_24 = ["onClick", "onDblclick"]
const _hoisted_25 = {
  key: 0,
  class: "modal-backdrop"
}
const _hoisted_26 = { class: "modal" }
const _hoisted_27 = { class: "modal-summary" }
const _hoisted_28 = { class: "modal-body form-layout" }
const _hoisted_29 = { class: "patient-picker" }
const _hoisted_30 = {
  key: 0,
  class: "patient-results"
}
const _hoisted_31 = ["onClick"]
const _hoisted_32 = { key: 0 }
const _hoisted_33 = { class: "wide treatment-picker" }
const _hoisted_34 = { class: "treatment-options" }
const _hoisted_35 = ["value"]
const _hoisted_36 = { class: "wide" }
const _hoisted_37 = { class: "wide" }

import { computed, reactive, ref, watch } from 'vue';

type ReservationStatus = '예약' | '확인' | '내원' | '완료' | '미이행' | '취소';
type Reservation = { id: number; chartNo: string; patient: string; phone: string; date: string; time: string; doctor: string; chair: string; treatment: string; treatmentItems: string[]; status: ReservationStatus; memo: string; changeLogs?: unknown[]; phoneMemos?: unknown[] };
type PatientOption = { id?: number; chartNo: string; name: string; phone: string };
type PatientRecord = PatientOption & { gender?: string; birth?: string; email?: string; address?: string; visitPath?: string; guardianName?: string; guardianRelation?: string; guardianPhone?: string; guardianMemo?: string; medicalHistory?: string[]; medications?: string; allergies?: string; memo?: string; privateMemo?: string; status?: string };

const storageKey = 'bioa-reservations';

export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarPage',
  setup(__props) {

const doctors = ['김원장', '박원장', '이실장', '데스크'];
const chairs = ['체어 1', '체어 2', '체어 3', '상담실'];
const statusOptions: ReservationStatus[] = ['예약', '확인', '내원', '완료', '미이행', '취소'];
const treatmentOptions = ['정기검진', '스케일링', '충치 치료', '신경 치료', '발치', '임플란트 상담', '임플란트 수술', '교정 상담', '보철', 'CT/방사선', '구강스캔'];
const timeSlots = Array.from({ length: 22 }, (_, index) => `${String(Math.floor(index / 2) + 9).padStart(2, '0')}:${index % 2 ? '30' : '00'}`);
const todayText = toDateText(new Date());
const baseDateText = ref(todayText);
const filterText = ref('');
const doctorFilters = ref([...doctors]);
const chairFilters = ref([...chairs]);
const statusFilter = ref<ReservationStatus | ''>('');
const reservations = ref<Reservation[]>(loadReservations());
const selectedReservationId = ref<number | null>(reservations.value[0]?.id || null);
const modalOpen = ref(false);
const isCreating = ref(false);
const editing = reactive<Reservation>(blankReservation(todayText, '09:00'));
const patientQuery = ref('');
const patientListOpen = ref(false);

const selectedReservation = computed(() => reservations.value.find((item) => item.id === selectedReservationId.value) || null);
const weekStart = computed(() => { const date = fromDateText(baseDateText.value); date.setDate(date.getDate() - date.getDay()); return date; });
const weekDays = computed(() => Array.from({ length: 7 }, (_, index) => { const date = new Date(weekStart.value); date.setDate(weekStart.value.getDate() + index); return { date: toDateText(date), weekday: ['일', '월', '화', '수', '목', '금', '토'][index], label: `${date.getMonth() + 1}/${date.getDate()}` }; }));
const weekTitle = computed(() => `${weekDays.value[0].date} ~ ${weekDays.value[6].date}`);
const filteredReservations = computed(() => {
  const query = filterText.value.trim().toLowerCase();
  return reservations.value.filter((item) => doctorFilters.value.includes(item.doctor) && chairFilters.value.includes(item.chair) && (!statusFilter.value || item.status === statusFilter.value) && (!query || [item.patient, item.phone, item.doctor, item.chair, item.treatment, item.status, item.memo].join(' ').toLowerCase().includes(query)));
});
const missedReservations = computed(() => reservations.value.filter((item) => item.status === '미이행'));
const patientOptions = computed(() => loadPatientOptions());
const filteredPatients = computed(() => {
  const query = patientQuery.value.trim().toLowerCase();
  if (!query) return patientOptions.value.slice(0, 30);
  return patientOptions.value.filter((item) => [item.name, item.chartNo, item.phone].join(' ').toLowerCase().includes(query)).slice(0, 50);
});

watch([doctorFilters, chairFilters], () => { if (!doctorFilters.value.length) doctorFilters.value = [...doctors]; if (!chairFilters.value.length) chairFilters.value = [...chairs]; });

function reservationsFor(date: string, time: string) { return filteredReservations.value.filter((item) => item.date === date && item.time === time); }
function openModal(reservation: Reservation | null, date = baseDateText.value, time = '09:00') { isCreating.value = !reservation; Object.assign(editing, reservation ? normalizeReservation(clone(reservation)) : blankReservation(date, time)); patientQuery.value = editing.chartNo ? `${editing.patient} ${editing.chartNo}` : editing.patient; modalOpen.value = true; }
function closeModal() { modalOpen.value = false; }
function saveReservation() {
  if (!editing.patient || !editing.date || !editing.time) return;
  if (!ensurePatientForReservation()) return;
  syncTreatmentText();
  if (isCreating.value) { editing.id = Date.now(); reservations.value.unshift(clone(editing)); selectedReservationId.value = editing.id; } else { const index = reservations.value.findIndex((item) => item.id === editing.id); if (index >= 0) reservations.value[index] = clone(editing); }
  saveReservations(); closeModal();
}
function setSelectedStatus(status: ReservationStatus) { if (!selectedReservation.value) return; selectedReservation.value.status = status; saveReservations(); }
function moveWeek(days: number) { const date = fromDateText(baseDateText.value); date.setDate(date.getDate() + days); baseDateText.value = toDateText(date); }
function goToday() { baseDateText.value = todayText; }
function selectPatient(patient: PatientOption) { editing.chartNo = patient.chartNo; editing.patient = patient.name; editing.phone = patient.phone; patientQuery.value = `${patient.name} ${patient.chartNo}`; patientListOpen.value = false; }
function syncTypedPatient() { editing.patient = patientQuery.value; }
function selectFirstPatient() { const first = filteredPatients.value[0]; if (first) selectPatient(first); }
function syncTreatmentText() { editing.treatment = editing.treatmentItems.join(', '); }
function blankReservation(date: string, time: string): Reservation { return { id: 0, chartNo: '', patient: '', phone: '', date, time, doctor: doctors[0], chair: chairs[0], treatment: '', treatmentItems: [], status: '예약', memo: '' }; }
function normalizeReservation(value: Partial<Reservation>): Reservation {
  const treatmentItems = Array.isArray(value.treatmentItems) ? value.treatmentItems : splitTreatment(value.treatment);
  return { ...blankReservation(todayText, '09:00'), ...value, treatmentItems, treatment: value.treatment || treatmentItems.join(', '), status: statusOptions.includes(value.status as ReservationStatus) ? value.status as ReservationStatus : '예약' };
}
function splitTreatment(value: unknown) { return typeof value === 'string' ? value.split(',').map((item) => item.trim()).filter(Boolean) : []; }
function ensurePatientForReservation() {
  const patients = loadPatientRecords();
  const patientName = cleanPatientName(editing.patient || patientQuery.value);
  if (!patientName) return false;
  const chartNo = editing.chartNo.trim();
  const found = patients.find((patient) => chartNo && sameText(patient.chartNo, chartNo)) || patients.find((patient) => sameText(patient.name, patientName) && (!editing.phone || !patient.phone || samePhone(patient.phone, editing.phone)));
  if (found) {
    selectPatient({ id: found.id, chartNo: found.chartNo, name: found.name, phone: found.phone || editing.phone });
    return true;
  }
  const shouldCreate = window.confirm(`${patientName} ${'\uD658\uC790\uB294 \uB4F1\uB85D\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.'}\n${'\uD658\uC790 \uB4F1\uB85D \uD6C4 \uC608\uC57D\uC744 \uC800\uC7A5\uD560\uAE4C\uC694?'}\n\n${'\uC5F0\uB77D\uCC98'}: ${editing.phone || '-'}`);
  if (!shouldCreate) return false;
  const patient = makePatientFromReservation(patients, patientName);
  patients.unshift(patient);
  savePatientRecords(patients);
  selectPatient({ id: patient.id, chartNo: patient.chartNo, name: patient.name, phone: patient.phone });
  return true;
}

function makePatientFromReservation(patients: PatientRecord[], patientName: string): PatientRecord {
  return {
    id: Date.now(),
    chartNo: editing.chartNo.trim() || nextPatientChartNo(patients),
    name: patientName,
    phone: editing.phone || '',
    gender: '\uBBF8\uC785\uB825',
    birth: '',
    email: '',
    address: '',
    visitPath: '\uC608\uC57D\uB4F1\uB85D',
    guardianName: '',
    guardianRelation: '',
    guardianPhone: '',
    guardianMemo: '',
    medicalHistory: [],
    medications: '',
    allergies: '',
    memo: '\uC608\uC57D \uB4F1\uB85D \uC911 \uC790\uB3D9 \uC0DD\uC131',
    privateMemo: '',
    status: '\uC815\uC0C1',
  };
}

function loadPatientRecords(): PatientRecord[] {
  try {
    return (JSON.parse(localStorage.getItem('bioa-patients') || '[]') as PatientRecord[]).filter((patient) => patient.name || patient.chartNo);
  } catch {
    return [];
  }
}

function savePatientRecords(patients: PatientRecord[]) { localStorage.setItem('bioa-patients', JSON.stringify(patients)); }
function nextPatientChartNo(patients: PatientRecord[]) { const max = patients.reduce((acc, patient) => Math.max(acc, Number((patient.chartNo || '').replace(/\D/g, '')) || 0), 1000); return `P-${max + 1}`; }
function cleanPatientName(value: string) { return value.trim().replace(/\s+P-\d+.*$/i, '').trim(); }
function sameText(a = '', b = '') { return a.trim().toLowerCase() === b.trim().toLowerCase(); }
function samePhone(a = '', b = '') { return a.replace(/\D/g, '') === b.replace(/\D/g, ''); }
function loadReservations() { try { return (JSON.parse(localStorage.getItem(storageKey) || '[]') as Partial<Reservation>[]).map(normalizeReservation); } catch { return []; } }
function loadPatientOptions(): PatientOption[] { try { return (JSON.parse(localStorage.getItem('bioa-patients') || '[]') as Array<Partial<PatientOption> & { name?: string }>).filter((item) => item.name || item.chartNo).map((item) => ({ id: item.id, chartNo: item.chartNo || `P-${item.id || 'TEMP'}`, name: item.name || '환자', phone: item.phone || '' })); } catch { return []; } }
function saveReservations() { localStorage.setItem(storageKey, JSON.stringify(reservations.value)); }
function statusClass(status: ReservationStatus) { return { 예약: 'reserved', 확인: 'confirmed', 내원: 'arrived', 완료: 'done', 미이행: 'missed', 취소: 'cancelled' }[status]; }
function toDateText(date: Date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
function fromDateText(value: string) { const [year, month, day] = value.split('-').map(Number); return new Date(year, month - 1, day); }
function clone<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _cache[28] || (_cache[28] = _createElementVNode("nav", { class: "tabs" }, [
        _createElementVNode("button", { class: "active" }, "예약 관리 캘린더")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openModal(null)))
        }, "예약 등록"),
        _createElementVNode("button", {
          disabled: !selectedReservation.value,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedReservation.value && openModal(selectedReservation.value)))
        }, "변경", 8, _hoisted_4),
        _createElementVNode("button", {
          disabled: !selectedReservation.value,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (setSelectedStatus('취소')))
        }, "취소", 8, _hoisted_5),
        _createElementVNode("button", { onClick: goToday }, "오늘"),
        _createElementVNode("label", _hoisted_6, [
          _cache[21] || (_cache[21] = _createElementVNode("span", null, "기준일", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((baseDateText).value = $event)),
            type: "date"
          }, null, 512), [
            [_vModelText, baseDateText.value]
          ])
        ]),
        _createElementVNode("label", _hoisted_7, [
          _cache[22] || (_cache[22] = _createElementVNode("span", null, "필터", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filterText).value = $event)),
            placeholder: "환자/진료/의료진/체어 검색"
          }, null, 512), [
            [_vModelText, filterText.value]
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_8, [
        _createElementVNode("aside", _hoisted_9, [
          _createElementVNode("section", _hoisted_10, [
            _cache[23] || (_cache[23] = _createElementVNode("header", null, "의료진별 예약", -1)),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(doctors, (doctor) => {
              return _createElementVNode("label", {
                key: doctor,
                class: "check-row"
              }, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((doctorFilters).value = $event)),
                  type: "checkbox",
                  value: doctor
                }, null, 8, _hoisted_11), [
                  [_vModelCheckbox, doctorFilters.value]
                ]),
                _createTextVNode(_toDisplayString(doctor), 1)
              ])
            }), 64))
          ]),
          _createElementVNode("section", _hoisted_12, [
            _cache[24] || (_cache[24] = _createElementVNode("header", null, "체어별 예약", -1)),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(chairs, (chair) => {
              return _createElementVNode("label", {
                key: chair,
                class: "check-row"
              }, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((chairFilters).value = $event)),
                  type: "checkbox",
                  value: chair
                }, null, 8, _hoisted_13), [
                  [_vModelCheckbox, chairFilters.value]
                ]),
                _createTextVNode(_toDisplayString(chair), 1)
              ])
            }), 64))
          ]),
          _createElementVNode("section", _hoisted_14, [
            _cache[25] || (_cache[25] = _createElementVNode("header", null, "예약 상태", -1)),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusOptions, (status) => {
              return _createElementVNode("button", {
                key: status,
                class: _normalizeClass([{ active: statusFilter.value === status }, "status-filter"]),
                onClick: ($event: any) => (statusFilter.value = statusFilter.value === status ? '' : status)
              }, [
                _createElementVNode("span", {
                  class: _normalizeClass(["dot", statusClass(status)])
                }, null, 2),
                _createTextVNode(" " + _toDisplayString(status) + " ", 1),
                _createElementVNode("strong", null, _toDisplayString(reservations.value.filter((item) => item.status === status).length), 1)
              ], 10, _hoisted_15)
            }), 64))
          ]),
          _createElementVNode("section", _hoisted_16, [
            _cache[26] || (_cache[26] = _createElementVNode("header", null, "예약 미이행", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(missedReservations.value, (reservation) => {
              return (_openBlock(), _createElementBlock("button", {
                key: reservation.id,
                class: "missed-card",
                onClick: ($event: any) => (openModal(reservation))
              }, [
                _createElementVNode("strong", null, _toDisplayString(reservation.patient), 1),
                _createElementVNode("span", null, _toDisplayString(reservation.date) + " " + _toDisplayString(reservation.time) + " · " + _toDisplayString(reservation.doctor), 1),
                _createElementVNode("em", null, _toDisplayString(reservation.phone), 1)
              ], 8, _hoisted_17))
            }), 128)),
            (!missedReservations.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_18, "미이행 예약이 없습니다."))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("section", _hoisted_19, [
          _createElementVNode("header", _hoisted_20, [
            _createElementVNode("button", {
              onClick: _cache[7] || (_cache[7] = ($event: any) => (moveWeek(-7)))
            }, "‹"),
            _createElementVNode("strong", null, _toDisplayString(weekTitle.value), 1),
            _createElementVNode("button", {
              onClick: _cache[8] || (_cache[8] = ($event: any) => (moveWeek(7)))
            }, "›")
          ]),
          _createElementVNode("div", _hoisted_21, [
            _cache[27] || (_cache[27] = _createElementVNode("div", { class: "time-head" }, "시간", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekDays.value, (day) => {
              return (_openBlock(), _createElementBlock("div", {
                key: day.date,
                class: "day-head"
              }, [
                _createElementVNode("span", null, _toDisplayString(day.weekday), 1),
                _createElementVNode("strong", null, _toDisplayString(day.label), 1)
              ]))
            }), 128)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timeSlots), (time) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: time }, [
                _createElementVNode("div", _hoisted_22, _toDisplayString(time), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekDays.value, (day) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `${day.date}-${time}`,
                    class: "reservation-cell",
                    onDblclick: ($event: any) => (openModal(null, day.date, time))
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reservationsFor(day.date, time), (reservation) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: reservation.id,
                        class: _normalizeClass(['reservation-card', statusClass(reservation.status), { selected: selectedReservationId.value === reservation.id }]),
                        onClick: _withModifiers(($event: any) => (selectedReservationId.value = reservation.id), ["stop"]),
                        onDblclick: _withModifiers(($event: any) => (openModal(reservation)), ["stop"])
                      }, [
                        _createElementVNode("strong", null, _toDisplayString(reservation.patient), 1),
                        _createElementVNode("span", null, _toDisplayString(reservation.treatment || '-'), 1),
                        _createElementVNode("em", null, _toDisplayString(reservation.doctor) + " · " + _toDisplayString(reservation.chair), 1)
                      ], 42, _hoisted_24))
                    }), 128))
                  ], 40, _hoisted_23))
                }), 128))
              ], 64))
            }), 128))
          ])
        ])
      ])
    ]),
    (modalOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
          _createElementVNode("section", _hoisted_26, [
            _createElementVNode("header", null, [
              _createElementVNode("strong", null, _toDisplayString(isCreating.value ? '예약 등록' : '예약 변경'), 1),
              _createElementVNode("button", { onClick: closeModal }, "×")
            ]),
            _createElementVNode("div", _hoisted_27, [
              _createElementVNode("div", null, [
                _cache[29] || (_cache[29] = _createElementVNode("span", null, "환자", -1)),
                _createElementVNode("strong", null, _toDisplayString(editing.patient || '환자명 입력'), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[30] || (_cache[30] = _createElementVNode("span", null, "차트번호", -1)),
                _createElementVNode("strong", null, _toDisplayString(editing.chartNo || '-'), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[31] || (_cache[31] = _createElementVNode("span", null, "예약일시", -1)),
                _createElementVNode("strong", null, _toDisplayString(editing.date) + " " + _toDisplayString(editing.time), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[32] || (_cache[32] = _createElementVNode("span", null, "담당", -1)),
                _createElementVNode("strong", null, _toDisplayString(editing.doctor) + " · " + _toDisplayString(editing.chair), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("label", _hoisted_29, [
                _cache[33] || (_cache[33] = _createElementVNode("span", null, "환자 *", -1)),
                _createElementVNode("div", null, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((patientQuery).value = $event)),
                    placeholder: "이름/차트/전화 검색 또는 직접 입력",
                    onFocus: _cache[10] || (_cache[10] = ($event: any) => (patientListOpen.value = true)),
                    onInput: syncTypedPatient,
                    onKeyup: _withKeys(selectFirstPatient, ["enter"])
                  }, null, 544), [
                    [_vModelText, patientQuery.value]
                  ]),
                  (patientListOpen.value && patientQuery.value.trim())
                    ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPatients.value, (patient) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: patient.chartNo,
                            type: "button",
                            onClick: ($event: any) => (selectPatient(patient))
                          }, [
                            _createElementVNode("strong", null, _toDisplayString(patient.name), 1),
                            _createElementVNode("span", null, _toDisplayString(patient.chartNo) + " · " + _toDisplayString(patient.phone || '-'), 1)
                          ], 8, _hoisted_31))
                        }), 128)),
                        (!filteredPatients.value.length)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_32, "등록된 환자가 없습니다."))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[34] || (_cache[34] = _createElementVNode("span", null, "차트번호", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editing.chartNo) = $event))
                }, null, 512), [
                  [_vModelText, editing.chartNo]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[35] || (_cache[35] = _createElementVNode("span", null, "연락처", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((editing.phone) = $event))
                }, null, 512), [
                  [_vModelText, editing.phone]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[36] || (_cache[36] = _createElementVNode("span", null, "예약일 *", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((editing.date) = $event)),
                  type: "date"
                }, null, 512), [
                  [_vModelText, editing.date]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[37] || (_cache[37] = _createElementVNode("span", null, "예약시간 *", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((editing.time) = $event))
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timeSlots), (time) => {
                    return (_openBlock(), _createElementBlock("option", { key: time }, _toDisplayString(time), 1))
                  }), 128))
                ], 512), [
                  [_vModelSelect, editing.time]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[38] || (_cache[38] = _createElementVNode("span", null, "의료진", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((editing.doctor) = $event))
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(doctors, (doctor) => {
                    return _createElementVNode("option", { key: doctor }, _toDisplayString(doctor), 1)
                  }), 64))
                ], 512), [
                  [_vModelSelect, editing.doctor]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[39] || (_cache[39] = _createElementVNode("span", null, "체어", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((editing.chair) = $event))
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(chairs, (chair) => {
                    return _createElementVNode("option", { key: chair }, _toDisplayString(chair), 1)
                  }), 64))
                ], 512), [
                  [_vModelSelect, editing.chair]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[40] || (_cache[40] = _createElementVNode("span", null, "상태", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((editing.status) = $event))
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusOptions, (status) => {
                    return _createElementVNode("option", { key: status }, _toDisplayString(status), 1)
                  }), 64))
                ], 512), [
                  [_vModelSelect, editing.status]
                ])
              ]),
              _createElementVNode("label", _hoisted_33, [
                _cache[41] || (_cache[41] = _createElementVNode("span", null, "진료항목", -1)),
                _createElementVNode("div", _hoisted_34, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(treatmentOptions, (item) => {
                    return _createElementVNode("label", { key: item }, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((editing.treatmentItems) = $event)),
                        type: "checkbox",
                        value: item,
                        onChange: syncTreatmentText
                      }, null, 40, _hoisted_35), [
                        [_vModelCheckbox, editing.treatmentItems]
                      ]),
                      _createTextVNode(_toDisplayString(item), 1)
                    ])
                  }), 64))
                ])
              ]),
              _createElementVNode("label", _hoisted_36, [
                _cache[42] || (_cache[42] = _createElementVNode("span", null, "진료내용", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((editing.treatment) = $event)),
                  placeholder: "체크 항목 외 추가 내용 입력"
                }, null, 512), [
                  [_vModelText, editing.treatment]
                ])
              ]),
              _createElementVNode("label", _hoisted_37, [
                _cache[43] || (_cache[43] = _createElementVNode("span", null, "메모", -1)),
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((editing.memo) = $event)),
                  rows: "4"
                }, null, 512), [
                  [_vModelText, editing.memo]
                ])
              ])
            ]),
            _createElementVNode("footer", null, [
              _createElementVNode("button", {
                class: "ghost",
                onClick: closeModal
              }, "취소"),
              _createElementVNode("button", { onClick: saveReservation }, "저장")
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})